import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangleIcon, ArrowRightIcon } from "lucide-react";
import { ActivityList } from "@/components/activity-list";
import { EmptyState, PageHeader, Stat } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { platformLabel, timeAgo } from "@/lib/format";
import { useDevices, useUnlocked, useVault } from "@/lib/hooks";
import { isGone } from "@/lib/projects";

export const Route = createFileRoute("/_app/")({ component: OverviewPage });

function OverviewPage() {
  const s = useUnlocked();
  const v = useVault();
  const devices = useDevices();
  const snap = v.snapshot;
  const conflicts = snap?.conflicts.length ?? 0;
  const decisions = snap?.decisions.length ?? 0;
  const envKeys = snap?.files.reduce((n, f) => n + f.keys.filter((k) => k.value !== null).length, 0) ?? 0;
  const enrolled = devices.data ? new Set(devices.data.map((d) => d.id)) : null;
  const gone = snap?.projects.filter((p) => isGone(p, enrolled)).length ?? 0;
  return (
    <>
      <PageHeader
        title={`Hi ${s.account.name.split(" ")[0] ?? ""}`}
        description="Everything below is decrypted in this browser."
      />
      {decisions > 0 ? (
        <div
          className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4"
          data-testid="decisions-banner"
        >
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangleIcon className="size-4 text-warning" />
            {decisions} {decisions === 1 ? "decision is" : "decisions are"} waiting: second checkouts or differing
            copies of files that EnvStash will not touch until you choose.
          </div>
          <Button size="sm" variant="outline" render={<Link to="/projects" />}>
            Review <ArrowRightIcon />
          </Button>
        </div>
      ) : null}
      {conflicts > 0 ? (
        <div className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4">
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangleIcon className="size-4 text-warning" />
            {conflicts} conflicting {conflicts === 1 ? "value needs" : "values need"} a decision.
          </div>
          <Button size="sm" variant="outline" render={<Link to="/projects" />}>
            Review <ArrowRightIcon />
          </Button>
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Projects"
          value={snap ? snap.projects.length - gone : "–"}
          hint={gone > 0 ? `${gone} not on any Mac` : undefined}
        />
        <Stat label="Tracked files" value={snap?.files.length ?? "–"} hint={`${envKeys} env keys`} />
        <Stat label="Devices" value={devices.data?.length ?? "–"} />
        <Stat
          label="Sync"
          value={v.status === "live" ? "Live" : v.status}
          hint={`${snap?.op_count ?? 0} ops · last ${timeAgo(v.lastSyncAt)}`}
        />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Devices</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {devices.data?.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {d.name} {d.current ? <Badge variant="secondary">this browser</Badge> : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {platformLabel(d.platform)} · seen {timeAgo(d.last_seen_at)}
                  </p>
                </div>
              </div>
            ))}
            <Button variant="link" className="px-0" render={<Link to="/devices" />}>
              Manage devices <ArrowRightIcon />
            </Button>
          </CardContent>
        </Card>
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>Recent activity</CardTitle>
          </CardHeader>
          <CardContent>
            {v.activity.length === 0 ? (
              <EmptyState title="Nothing synced yet">
                Install the CLI on a Mac and run <code className="font-mono">envstash login</code> to start syncing.
              </EmptyState>
            ) : (
              <ActivityList items={v.activity.slice(0, 12)} compact />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
